// Store d'usage FICTIF de l'écran Settings → Usage (maquette UI, aucun
// backend) : compteurs en % persistés en localStorage, +0,1 % par action
// (génération image/vidéo, entraînement LoRA), et historique « ticket de
// caisse » des événements affiché par le bouton Details.
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getModelLabel } from '../constants/models';

const USAGE_STORAGE_KEY = 'rabbit-usage-v2';

// Incrément d'usage appliqué à chaque run / entraînement (maquette).
export const USAGE_STEP_PERCENT = 0.1;

// L'historique local reste borné — ticket de caisse, pas un data lake.
const MAX_USAGE_EVENTS = 200;

export type UsageEventType = 'lora-training' | 'image' | 'video';

export interface UsageEvent {
  id: string;
  type: UsageEventType;
  /** Libellé « ticket de caisse » (ex. « Image — Krea 2 Turbo »). */
  label: string;
  /** Date de l'événement (ISO string — stockage local uniquement). */
  createdAt: string;
}

interface UsagePersistedState {
  imageUsage: number;
  videoUsage: number;
  loraUsage: number;
  weeklyUsage: number;
  events: UsageEvent[];
}

// Valeurs fictives de départ de la maquette : compteurs à zéro.
const DEFAULT_USAGE_STATE: UsagePersistedState = {
  imageUsage: 0,
  videoUsage: 0,
  loraUsage: 0,
  weeklyUsage: 0,
  events: [],
};

const loadUsageState = (): UsagePersistedState => {
  if (typeof window === 'undefined') return DEFAULT_USAGE_STATE;
  try {
    const stored = window.localStorage.getItem(USAGE_STORAGE_KEY);
    if (!stored) return DEFAULT_USAGE_STATE;
    return { ...DEFAULT_USAGE_STATE, ...(JSON.parse(stored) as Partial<UsagePersistedState>) };
  } catch {
    return DEFAULT_USAGE_STATE;
  }
};

const persistUsageState = (state: UsagePersistedState): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
};

// Évite la dérive des flottants (0,1 + 0,1 + …) — 2 décimales affichées.
const bump = (value: number): number => Math.round((value + USAGE_STEP_PERCENT) * 100) / 100;

interface UsageStore extends UsagePersistedState {
  logImageGeneration: (model: string) => void;
  logVideoGeneration: (duration: number) => void;
  logLoraTraining: (name?: string) => void;
}

export const useUsageStore = create<UsageStore>()(
  devtools(
    (set, get) => ({
      ...loadUsageState(),

      logImageGeneration: (model) => {
        const event: UsageEvent = {
          id: `usage-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'image',
          label: `Image — ${getModelLabel(model)}`,
          createdAt: new Date().toISOString(),
        };
        set((state) => {
          const next = {
            imageUsage: bump(state.imageUsage),
            weeklyUsage: bump(state.weeklyUsage),
            events: [event, ...state.events].slice(0, MAX_USAGE_EVENTS),
          };
          persistUsageState({ ...get(), ...next });
          return next;
        });
      },

      logVideoGeneration: (duration) => {
        const event: UsageEvent = {
          id: `usage-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'video',
          label: `Video — ${duration}s (LTX 2.5)`,
          createdAt: new Date().toISOString(),
        };
        set((state) => {
          const next = {
            videoUsage: bump(state.videoUsage),
            weeklyUsage: bump(state.weeklyUsage),
            events: [event, ...state.events].slice(0, MAX_USAGE_EVENTS),
          };
          persistUsageState({ ...get(), ...next });
          return next;
        });
      },

      logLoraTraining: (name) => {
        const event: UsageEvent = {
          id: `usage-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'lora-training',
          label: `Rabbit Training${name ? ` — ${name}` : ''}`,
          createdAt: new Date().toISOString(),
        };
        set((state) => {
          // Le training a son propre compteur (segment jaune « Rabbit Custom
          // Models » de la barre empilée, revue PO).
          const next = {
            loraUsage: bump(state.loraUsage),
            weeklyUsage: bump(state.weeklyUsage),
            events: [event, ...state.events].slice(0, MAX_USAGE_EVENTS),
          };
          persistUsageState({ ...get(), ...next });
          return next;
        });
      },
    }),
    { name: 'usage-store' }
  )
);
